import type { ConfigField, ConfigSchema } from '../types/presentation.types'

/**
 * A starting config for a newly placed block (MYD-495).
 *
 * A block has to be saveable the moment it lands on a slide, and the server
 * rejects a config missing a required field. So required fields get a
 * placeholder the user will obviously replace ("Heading", "First point"), and
 * optional ones take the schema's default. The same config drives the element
 * picker's live preview, so what the preview shows is what gets placed.
 *
 * Returns `null` when no valid starting config can be made up, which today
 * means a required URL: there is no honest placeholder for "your image". The
 * picker then places the block for the user to finish in the inspector, and
 * the save validation names the missing field if they do not.
 */

const PLACEHOLDER_TEXT: Record<string, string> = {
	text: 'Heading',
	markdown: 'Write something **important** here.',
	label: 'Label',
	value: '0',
}

const sampleField = (name: string, field: ConfigField): { ok: true; value: unknown } | { ok: false } => {
	if (!field.required) return field.default === undefined ? { ok: true, value: undefined } : { ok: true, value: structuredClone(field.default) }

	switch (field.type) {
		case 'string':
		case 'markdown':
			return { ok: true, value: (PLACEHOLDER_TEXT[name] ?? 'Text').slice(0, field.max) }
		case 'number':
			return { ok: true, value: field.min ?? 0 }
		case 'boolean':
			return { ok: true, value: false }
		case 'enum':
			return { ok: true, value: field.values[0] }
		case 'url':
			return { ok: false }
		case 'list': {
			const items: unknown[] = []
			for (let index = 0; index < Math.min(2, field.max); index += 1) {
				if (field.of.type === 'string') {
					items.push(`${index === 0 ? 'First' : 'Second'} point`)
					continue
				}
				const item = sampleField(name, { ...field.of, required: true })
				if (!item.ok) return { ok: false }
				items.push(item.value)
			}
			return { ok: true, value: items }
		}
		case 'object': {
			const nested = sampleConfig(field.fields)
			return nested ? { ok: true, value: nested } : { ok: false }
		}
		default:
			return { ok: false }
	}
}

export const sampleConfig = (schema: ConfigSchema): Record<string, unknown> | null => {
	const config: Record<string, unknown> = {}
	for (const [name, field] of Object.entries(schema)) {
		const sample = sampleField(name, field)
		if (!sample.ok) return null
		if (sample.value !== undefined) config[name] = sample.value
	}
	return config
}
